import { Fragment, ReactElement, ReactNode } from 'react';
import type { BlockDetails } from 'modules/campaign/block/types';
import type { ActivityType } from 'types';
import { formatDate, nl2br } from 'utilities';
import { ClipLabel } from 'modules/radio/ClipBadge';

const bool = (v: any): ReactNode => (v ? 'Yes' : 'No');

function RadioFields({ radio_clips }: BlockDetails<'RADIO'>): ReactElement | null {
  if (!radio_clips.length) return null;

  return (
    <>
      <dt>Radio clips</dt>
      <dd data-test-id="field-radio_clips">
        <ul className="mb-0">
          {radio_clips.map((clip) => (
            <li key={clip.id}>
              <ClipLabel clip={clip} />
            </li>
          ))}
        </ul>
      </dd>
    </>
  );
}

const genericFields: Partial<
  Record<
    ActivityType,
    {
      key: string;
      label: ReactNode;
      render?: (v: any) => ReactNode;
    }[]
  >
> = {
  TV: [
    { key: 'clip_length', label: 'Clip length (seconds)' },
    { key: 'yearly_budget_by_group', label: 'Yearly budget by media group' },
    { key: 'agreed_prices', label: 'Agreed prices' },
  ],
  WEB: [
    { key: 'suggested_format', label: 'Suggested format' },
    { key: 'video', label: 'Video, clip length' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'kpi', label: 'KPI' },
  ],
  RTB: [{ key: 'kpi', label: 'KPI' }],
  SEO: [
    { key: 'website', label: 'Website' },
    { key: 'public', label: 'Public', render: bool },
    { key: 'indexed', label: 'Indexed', render: bool },
    { key: 'audit', label: 'Audit', render: bool },
    { key: 'consultations', label: 'Consultations', render: bool },
    { key: 'meta_tags', label: 'Meta tags', render: bool },
    { key: 'content', label: 'Content', render: bool },
    { key: 'backlinks', label: 'Backlinks', render: bool },
    { key: 'articles', label: 'Articles', render: bool },
    { key: 'reports', label: 'Reports', render: bool },
  ],
  CREATIVE: [
    { key: 'first_sketch', label: 'First sketch', render: formatDate },
    {
      key: 'materials',
      label: 'Materials',
      render: (materials: any[]) => (
        <ul className="mb-0">
          {materials.map(({ kind, amount }, idx) => (
            <li key={idx}>
              {kind} × {amount}
            </li>
          ))}
        </ul>
      ),
    },
    { key: 'styleguide', label: 'Style guidelines', render: nl2br },
    { key: 'copywrite', label: 'Copywrite', render: nl2br },
    { key: 'website', label: 'Website', render: nl2br },
    { key: 'social_media', label: 'WebsiSocial mediate', render: nl2br },
  ],
};

export function Fields(block: BlockDetails): ReactElement {
  const { type, fields } = block;

  return (
    <dl>
      {type === 'RADIO' && <RadioFields {...block} />}
      {(genericFields[type] || []).map(({ key, label, render }) => {
        const value = fields[key];
        if (!value) return null;
        return (
          <Fragment key={key}>
            <dt>{label}</dt>
            <dd data-test-id={`field-${key}`}>{render ? render(fields[key]) : fields[key]}</dd>
          </Fragment>
        );
      })}
    </dl>
  );
}
